"use client";

import { useIsMobile } from "@/hooks/breakpoints";
import type React from "react";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type SearchTriggerContextType = {
  isAnyTriggerOpen: boolean;
  activeTrigger: string | null;
  setTriggerOpen: (id: string, isOpen: boolean) => void;
  hasPendingChanges: boolean;
  setHasPendingChanges: (hasChanges: boolean) => void;
  resetAllTriggers: () => void;
  initialTrigger: "activity" | "location" | null;
  setInitialTrigger: (trigger: "activity" | "location" | null) => void;
};

const SearchTriggerContext = createContext<
  SearchTriggerContextType | undefined
>(undefined);

export function SearchTriggerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeTrigger, setActiveTrigger] = useState<string | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [wasRecentlyInteracted, setWasRecentlyInteracted] = useState(false);
  const [initialTrigger, setInitialTrigger] = useState<
    "activity" | "location" | null
  >(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();

  const isAnyTriggerOpen =
    activeTrigger !== null || hasPendingChanges || wasRecentlyInteracted;

  useEffect(() => {
    // Prevent scrollbar shift during dropdown transitions on desktop
    // wasRecentlyInteracted ensures scrollbar lock persists when switching between dropdowns
    const shouldLock = !isMobile && isAnyTriggerOpen;

    if (shouldLock) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty(
        "--scrollbar-width",
        `${scrollbarWidth}px`,
      );
      document.body.classList.add("scroll-locked");
    } else {
      document.body.classList.remove("scroll-locked");
      document.documentElement.style.removeProperty("--scrollbar-width");
    }

    return () => {
      document.body.classList.remove("scroll-locked");
      document.documentElement.style.removeProperty("--scrollbar-width");
    };
  }, [isMobile, isAnyTriggerOpen]);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setTriggerOpen = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        clearTimeoutRef();
        setWasRecentlyInteracted(true);
        setActiveTrigger(id);
      } else {
        // Only close if this trigger is currently active
        setActiveTrigger((current) => (current === id ? null : current));
        clearTimeoutRef();
        timeoutRef.current = setTimeout(() => {
          setWasRecentlyInteracted(false);
        }, 100);
      }
    },
    [clearTimeoutRef],
  );

  const resetAllTriggers = useCallback(() => {
    setActiveTrigger(null);
    setHasPendingChanges(false);
    clearTimeoutRef();
    setWasRecentlyInteracted(false);
    setInitialTrigger(null);
  }, [clearTimeoutRef]);

  useEffect(() => {
    return () => {
      clearTimeoutRef();
    };
  }, [clearTimeoutRef]);

  const value = useMemo(
    () => ({
      isAnyTriggerOpen,
      activeTrigger,
      setTriggerOpen,
      hasPendingChanges,
      setHasPendingChanges,
      resetAllTriggers,
      initialTrigger,
      setInitialTrigger,
    }),
    [
      isAnyTriggerOpen,
      activeTrigger,
      setTriggerOpen,
      hasPendingChanges,
      resetAllTriggers,
      initialTrigger,
    ],
  );

  return (
    <SearchTriggerContext.Provider value={value}>
      {children}
    </SearchTriggerContext.Provider>
  );
}

export function useSearchTriggerContext() {
  const context = use(SearchTriggerContext);
  if (context === undefined) {
    throw new Error(
      "useSearchTriggerContext must be used within a SearchTriggerProvider",
    );
  }
  return context;
}
